import { useCallback, type RefObject } from 'react';
import html2canvas from 'html2canvas';
import { embedPNGMetadata, embedSVGMetadata, type ExportMetadata } from '@alchemiesolns/image-metadata';
import { fromConfigString, toOpaqueString } from '@alchemiesolns/workspace-serialization';
import {
  pickSaveTarget,
  commitBlobToSaveTarget,
  PNG_SAVE_ACCEPT,
  SVG_SAVE_ACCEPT,
  generateSvgContent,
  waitForNextFrame,
  delay,
} from '../utils/downloadUtils';
import type { ArrowType } from '../components/ArrowSection';
import type { TemplateMode } from '../components/BatchUpload';
import {
  SCHEME_FONT_SIZE_DEFAULT,
  SCHEME_ZOOM_PERCENT_DEFAULT,
} from '../utils/schemeZoom';
import type { OverlayTextHorizontalAlign } from '../components/OverlayTextSvg';

/** Read the live `viewBox` attributes from the export DOM so PNG/SVG metadata matches the raster. */
function readDisplayedMoleculeViewBoxes(
  exportRoot: HTMLElement,
  templateMode: TemplateMode,
): { reactantViewBox: string | null; productViewBox: string | null } {
  if (templateMode === 'molecule') {
    const svg = exportRoot.querySelector('svg');
    const vb = svg?.getAttribute('viewBox')?.trim();
    return { reactantViewBox: vb && vb.length > 0 ? vb : null, productViewBox: null };
  }
  const rSvg = exportRoot.querySelector('#reactant-molecule svg');
  const pSvg = exportRoot.querySelector('#product-molecule svg');
  return {
    reactantViewBox: rSvg?.getAttribute('viewBox')?.trim() || null,
    productViewBox: pSvg?.getAttribute('viewBox')?.trim() || null,
  };
}

export interface SchemeExportState {
  templateMode: TemplateMode;
  reactantConfig: string;
  productConfig: string;
  topText: string;
  bottomText: string;
  arrowType: ArrowType;
  altText: string;
  reactantOverlayText?: string;
  productOverlayText?: string;
  reactantOverlayPosition?: { x: number; y: number } | null;
  productOverlayPosition?: { x: number; y: number } | null;
  reactantOverlayTextAlign?: OverlayTextHorizontalAlign;
  productOverlayTextAlign?: OverlayTextHorizontalAlign;
  exportWidth: number;
  exportHeight: number;
  reactantZoom: number;
  schemeDisplayZoom?: number;
  schemeFontSize?: number;
  schemeZoomPercent?: number;
  reactantViewBox?: string | null;
  productViewBox?: string | null;
}

export function useSchemeExport(
  state: SchemeExportState,
  exportRef: RefObject<HTMLDivElement | null>,
) {
  const isCanvasLikelyBlank = (canvas: HTMLCanvasElement): boolean => {
    const ctx = canvas.getContext('2d');
    if (!ctx) return true;
    const { width, height } = canvas;
    if (width === 0 || height === 0) return true;

    // Sample a coarse grid to avoid scanning every pixel.
    const sampleCols = 8;
    const sampleRows = 8;
    for (let row = 0; row < sampleRows; row += 1) {
      for (let col = 0; col < sampleCols; col += 1) {
        const x = Math.min(width - 1, Math.floor((col / (sampleCols - 1)) * (width - 1)));
        const y = Math.min(height - 1, Math.floor((row / (sampleRows - 1)) * (height - 1)));
        const [r, g, b, a] = ctx.getImageData(x, y, 1, 1).data;
        // Not pure white/transparent => likely contains rendered content.
        if (!(a === 0 || (r >= 250 && g >= 250 && b >= 250))) {
          return false;
        }
      }
    }
    return true;
  };

  /**
   * Build export metadata from app state. Optional `domSnapshot` is the live SVG viewBox
   * from the preview (preferred) so imports match the PNG even when the user never panned
   * (default zoom/alignment still lives on the `<svg viewBox>` only).
   */
  const buildExportMetadata = useCallback(
    (domSnapshot?: { reactantViewBox: string | null; productViewBox: string | null }): ExportMetadata => {
      const meta: ExportMetadata = {
        description: state.altText || undefined,
        author: 'Alchemie Solutions, Inc.', // PIPH-25: read from license key
        software: 'Alchemie Piph Authoring',
      };

      const reactantVb =
        (domSnapshot?.reactantViewBox?.trim() || '') ||
        (state.reactantViewBox?.trim() || '') ||
        '';
      const productVb =
        (domSnapshot?.productViewBox?.trim() || '') || (state.productViewBox?.trim() || '') || '';

      // Convert config strings to versioned opaque strings for metadata.
      // decodeWorkspace() on the import side handles both opaque and legacy config strings,
      // so old exports continue to work.
      const toOpaque = (config: string): string => {
        if (!config) return '';
        try {
          return toOpaqueString(fromConfigString(config));
        } catch {
          return config;
        } // fallback: store raw config if parse fails
      };

      if (state.templateMode === 'molecule') {
        meta.alchemieData = {
          templateMode: 'molecule',
          molecule: toOpaque(state.reactantConfig),
          ...(state.reactantOverlayText && { reactantOverlayText: state.reactantOverlayText }),
          ...(state.reactantOverlayText && state.reactantOverlayPosition != null && {
            reactantOverlayPosition: state.reactantOverlayPosition,
          }),
          ...(state.reactantOverlayText && state.reactantOverlayTextAlign != null && {
            reactantOverlayTextAlign: state.reactantOverlayTextAlign,
          }),
          ...(reactantVb ? { reactantViewBox: reactantVb } : {}),
        };
      } else {
        meta.alchemieData = {
          templateMode: 'reaction',
          reactant: toOpaque(state.reactantConfig),
          product: toOpaque(state.productConfig),
          reagents: state.topText,
          conditions: state.bottomText,
          arrowType: state.arrowType,
          ...(state.reactantOverlayText && { reactantOverlayText: state.reactantOverlayText }),
          ...(state.productOverlayText && { productOverlayText: state.productOverlayText }),
          ...(state.reactantOverlayText && state.reactantOverlayPosition != null && {
            reactantOverlayPosition: state.reactantOverlayPosition,
          }),
          ...(state.productOverlayText && state.productOverlayPosition != null && {
            productOverlayPosition: state.productOverlayPosition,
          }),
          ...(state.reactantOverlayText && state.reactantOverlayTextAlign != null && {
            reactantOverlayTextAlign: state.reactantOverlayTextAlign,
          }),
          ...(state.productOverlayText && state.productOverlayTextAlign != null && {
            productOverlayTextAlign: state.productOverlayTextAlign,
          }),
          ...(reactantVb ? { reactantViewBox: reactantVb } : {}),
          ...(productVb ? { productViewBox: productVb } : {}),
          schemeDisplayZoom: state.schemeDisplayZoom ?? 1,
          schemeFontSize: state.schemeFontSize ?? SCHEME_FONT_SIZE_DEFAULT,
          schemeZoomPercent: state.schemeZoomPercent ?? SCHEME_ZOOM_PERCENT_DEFAULT,
        };
      }

      return meta;
    },
    [
      state.altText,
      state.templateMode,
      state.reactantConfig,
      state.productConfig,
      state.topText,
      state.bottomText,
      state.arrowType,
      state.reactantViewBox,
      state.productViewBox,
      state.schemeDisplayZoom,
      state.schemeFontSize,
      state.schemeZoomPercent,
      state.reactantOverlayText,
      state.productOverlayText,
      state.reactantOverlayPosition,
      state.productOverlayPosition,
      state.reactantOverlayTextAlign,
      state.productOverlayTextAlign,
    ],
  );

  /** Export as PNG with embedded metadata. Returns whether a file was written (false if user cancelled save). */
  const handleDownloadPng = useCallback(async (filename: string): Promise<boolean> => {
    if (!exportRef.current) return false;

    const defaultName = state.templateMode === 'molecule' ? 'molecule' : 'reaction-scheme';
    const suggestedName = `${filename || defaultName}.png`;
    const saveTarget = await pickSaveTarget(suggestedName, PNG_SAVE_ACCEPT);
    if (saveTarget.kind === 'aborted') {
      return false;
    }

    await waitForNextFrame();
    // Ensure custom fonts are ready before rasterization; missing font metrics
    // can shift label baselines in html2canvas output.
    if ('fonts' in document) {
      try {
        await document.fonts.ready;
      } catch {
        // Best-effort only; continue export.
      }
    }

    const captureScale = 2;
    const exportElement = exportRef.current;
    /** Logical export pixels (preview parent uses CSS transform scale — getBoundingClientRect is wrong). */
    const layoutWidth = Math.max(1, Math.round(exportElement.offsetWidth || state.exportWidth));
    const layoutHeight = Math.max(1, Math.round(exportElement.offsetHeight || state.exportHeight));

    const prepareCloneForCapture = (clonedDoc: Document, referenceElement: HTMLElement) => {
      // Strip only the live-preview wrapper scale (direct parent in ReactionScheme). Keeps schemeDisplayZoom etc. inside the export root.
      const previewScaleWrap = referenceElement.parentElement;
      if (previewScaleWrap instanceof HTMLElement) {
        previewScaleWrap.style.transform = 'none';
      }

      const clonedRoot = exportElement.id ? clonedDoc.getElementById(exportElement.id) : null;

      // Empty-slot “Edit …” UI is for the interactive preview only; raster export should look blank.
      if (clonedRoot) {
        clonedRoot.querySelectorAll('.preview-slot-drop-target').forEach((node) => {
          (node as HTMLElement).style.setProperty('display', 'none', 'important');
        });
      }

      const labelNodes = clonedRoot
        ? Array.from(clonedRoot.querySelectorAll('.text-offset'))
        : Array.from(clonedDoc.querySelectorAll('.text-offset'));
      labelNodes.forEach((node) => {
        const el = node as HTMLElement;
        const prevTransform = el.style.transform || '';
        // html2canvas tends to place these labels low; nudge up in capture only.
        // -8 CSS px ~= 16 px on the 2x capture canvas.
        el.style.transform = `${prevTransform} translateY(-8px)`.trim();
      });
    };

    const captureOptions = {
      backgroundColor: '#ffffff',
      scale: captureScale,
      width: layoutWidth,
      height: layoutHeight,
      useCORS: true,
      onclone: prepareCloneForCapture,
    } as const;

    let canvas: HTMLCanvasElement;
    try {
      canvas = await html2canvas(exportElement, {
        ...captureOptions,
        // Better text baseline fidelity in many browsers.
        foreignObjectRendering: true,
      });
      if (isCanvasLikelyBlank(canvas)) {
        throw new Error('html2canvas produced blank canvas with foreignObjectRendering');
      }
    } catch (error) {
      console.warn('[useSchemeExport] PNG foreignObject capture failed; falling back.', error);
      canvas = await html2canvas(exportElement, {
        ...captureOptions,
        foreignObjectRendering: false,
      });
    }

    const rasterBlob = await new Promise<Blob>((resolve) => {
      canvas.toBlob((b) => resolve(b!), 'image/png');
    });
    const rawBuffer = new Uint8Array(await rasterBlob.arrayBuffer());
    const domSnapshot = readDisplayedMoleculeViewBoxes(exportElement, state.templateMode);
    const enhanced = embedPNGMetadata(rawBuffer, buildExportMetadata(domSnapshot));

    const outBlob = new Blob([enhanced], { type: 'image/png' });
    return commitBlobToSaveTarget(saveTarget, outBlob);
  }, [exportRef, buildExportMetadata, state.templateMode, state.topText, state.bottomText, state.exportWidth, state.exportHeight]);

  /** Export as SVG with embedded metadata. Returns whether a file was written (false if user cancelled save). */
  const handleDownloadSvg = useCallback(async (filename: string): Promise<boolean> => {
    if (!exportRef.current) return false;

    const defaultName = state.templateMode === 'molecule' ? 'molecule' : 'reaction-scheme';
    const suggestedName = `${filename || defaultName}.svg`;
    const saveTarget = await pickSaveTarget(suggestedName, SVG_SAVE_ACCEPT);
    if (saveTarget.kind === 'aborted') {
      return false;
    }

    try {
      await waitForNextFrame();
      await delay(100);

      const svgContent = await generateSvgContent({
        exportElement: exportRef.current,
        topText: state.templateMode === 'molecule' ? '' : state.topText,
        bottomText: state.templateMode === 'molecule' ? '' : state.bottomText,
        arrowType: state.templateMode === 'molecule' ? 'single' : state.arrowType,
        templateMode: state.templateMode,
        exportWidth: state.exportWidth,
        exportHeight: state.exportHeight,
        reactantZoom: state.reactantZoom,
        schemeDisplayZoom: state.schemeDisplayZoom ?? 1,
      });

      const domSnapshot = readDisplayedMoleculeViewBoxes(exportRef.current, state.templateMode);
      const enhancedSvg = embedSVGMetadata(svgContent, buildExportMetadata(domSnapshot));
      const blob = new Blob([enhancedSvg], { type: 'image/svg+xml' });
      return commitBlobToSaveTarget(saveTarget, blob);
    } catch (error) {
      // Avoid "nothing happens" failures: export the first SVG as a fallback.
      console.error('[useSchemeExport] SVG export failed; using fallback export.', error);
      const fallbackSvg = exportRef.current.querySelector('svg');
      if (!fallbackSvg) return false;
      const serialized = new XMLSerializer().serializeToString(fallbackSvg);
      const enhancedFallback = embedSVGMetadata(serialized, buildExportMetadata());
      const blob = new Blob([enhancedFallback], { type: 'image/svg+xml' });
      return commitBlobToSaveTarget(saveTarget, blob);
    }
  }, [
    exportRef,
    buildExportMetadata,
    state.templateMode,
    state.topText,
    state.bottomText,
    state.altText,
    state.arrowType,
    state.exportWidth,
    state.exportHeight,
    state.reactantZoom,
    state.schemeDisplayZoom,
    state.schemeFontSize,
    state.schemeZoomPercent,
  ]);

  return {
    handleDownloadPng,
    handleDownloadSvg,
  };
}
